/**
 * Split a caption line around the words the reader searched for.
 *
 * This runs in the browser on purpose. The server returns the matched *line*,
 * not a pre-marked string: the same line has to render differently depending on
 * what the reader typed, and a highlight that arrives as HTML is a stored-XSS
 * surface for every transcript the ingest pipeline ever writes.
 *
 * Matching mirrors what the reference product does:
 * - **Literal, not fuzzy.** The query is escaped, never treated as a pattern.
 * - **Whole words only.** `hell` must not light up inside `hello`. Word
 *   characters are Unicode letters and digits, so accented and Vietnamese text
 *   still breaks on spaces and punctuation rather than mid-word.
 * - **Flexible whitespace between query words.** A transcript says "happy  new
 *   year" with a double space; the reader typed one.
 * - **Multi-word queries must stay adjacent**, which is what makes the
 *   highlight agree with the RPC's `phraseto_tsquery`.
 */

export interface CaptionSegment {
    /** A run of text. */
    text: string;
    /** Whether this run is one of the query's occurrences. */
    match: boolean;
}

/** Unicode letter or digit — the characters a word is made of. */
const WORD_CHAR = "\\p{L}\\p{N}";

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Build the matcher for `query`, or `null` when there is nothing worth
 * highlighting.
 *
 * The leading `(^|[^word])` group exists so the boundary is *consumed and
 * re-emitted* as ordinary text instead of relying on a look-behind, which older
 * Safari throws on at construction time — a thrown `new RegExp` would blank the
 * whole result list, not just the highlight.
 */
function buildPattern(query: string): RegExp | null {
    const trimmed = query.trim();
    if (!trimmed) return null;

    const body = trimmed.split(/\s+/).map(escapeRegExp).join("\\s+");

    try {
        return new RegExp(`(^|[^${WORD_CHAR}])(${body})(?=$|[^${WORD_CHAR}])`, "giu");
    } catch {
        return null;
    }
}

/**
 * Split `text` into alternating plain and matched runs.
 *
 * Always returns at least one segment for non-empty text, so callers can map
 * over the result unconditionally instead of checking for `null`.
 */
export function splitCaptionByQuery(text: string, query: string): CaptionSegment[] {
    if (!text) return [];

    const pattern = buildPattern(query);
    if (!pattern) return [{ text, match: false }];

    const segments: CaptionSegment[] = [];
    let cursor = 0;

    for (const match of text.matchAll(pattern)) {
        const prefix = match[1] ?? "";
        const start = (match.index ?? 0) + prefix.length;
        const end = start + (match[2] ?? "").length;

        // A degenerate match would loop forever on the same index.
        if (end <= start) continue;

        if (start > cursor) {
            segments.push({ text: text.slice(cursor, start), match: false });
        }
        segments.push({ text: text.slice(start, end), match: true });
        cursor = end;
    }

    if (cursor === 0) return [{ text, match: false }];
    if (cursor < text.length) {
        segments.push({ text: text.slice(cursor), match: false });
    }

    return segments;
}
